import { LOGIN_PRESETS } from '@/config/loginPresets';

/**
 * THE SIGN-IN HERO'S THREE PRESETS — `g_set_login.dc.html`.
 *
 * The board draws three chips and names them by what they make: a midnight
 * gradient with a gold, a violet or an emerald glow in it. The product already
 * ships eight curated login pairs in `src/config/loginPresets.ts`, and three of
 * them ARE these three — so the colours below are the product's own, looked up
 * by id, and only the NAMES are the board's. Nothing new is invented, and the
 * config file (which this lane may not edit) stays the single source of the
 * hexes the sign-in page actually paints.
 */
export interface LoginChip {
  id: string;
  label: string;
  primary: string;
  accent: string;
}

const byId = (id: string) => LOGIN_PRESETS.find((p) => p.id === id);

export const LOGIN_CHIPS: LoginChip[] = (
  [
    ['ocean', 'Midnight & gold'],
    ['violet', 'Midnight & violet'],
    ['emerald', 'Midnight & emerald'],
  ] as const
).flatMap(([id, label]) => {
  const p = byId(id);
  return p ? [{ id, label, primary: p.primary, accent: p.accent }] : [];
});

/** What the sign-in page paints when a clinic has set no login colour at all. */
export const LOGIN_FALLBACK_PRIMARY = '#0c87f0';
export const LOGIN_FALLBACK_ACCENT = '#001a3a';

/**
 * THE FOUR DOCUMENT TEMPLATES — `g_set_docs.dc.html`.
 *
 * `id` is the value `tenant_brandings.pdf_template` carries and `pdfBrand.ts`
 * dispatches on. Three of the board's four rows are templates the PDF layer
 * really draws; the fourth is not:
 *
 *   Editorial  → `editorial`  headerEditorial with the colour band
 *   Classic    → `letterhead` headerLetterhead, the centred classic head
 *   Minimal    → `minimal`    the editorial structure with the band suppressed
 *   Letterhead → `preprinted` headerPreprinted draws no clinic identity and
 *                             leaves the top 4 cm blank for pre-printed paper
 */
export interface DocTemplate {
  /** The stored value, or null while nothing in the PDF layer draws this. */
  id: string | null;
  title: string;
  sub: string;
}

export const DOC_TEMPLATES: DocTemplate[] = [
  {
    id: 'editorial',
    title: 'Editorial',
    sub: 'modern · a coloured band at the head',
  },
  {
    id: 'letterhead',
    title: 'Classic',
    sub: 'serif head, ruled body — the shape most Egyptian practices print',
  },
  {
    id: 'minimal',
    title: 'Minimal',
    sub: 'no band, no rules — the cheapest to print',
  },
  {
    id: 'preprinted',
    title: 'Letterhead',
    sub: 'leaves the top 4 cm blank for pre-printed paper',
  },
];

/** Everything a template applies to, once it is the clinic's one. */
export const DOC_APPLIES_TO =
  'prescriptions · receipts · certificates · referral letters · payslips';
